'use client';

import { useEffect, useState } from 'react';
import { 
  getUTMAttribution,
  saveUTMAttributionOnRegistration,
  updateUTMAttributionOnLogin,
  getUserAttributionReport,
  type UTMAttribution 
} from '@/lib/utm-attribution';
import useAuthStore from '@/store/useAuthStore';

interface UseUTMTrackingReturn {
  attribution: UTMAttribution | null;
  associateWithUser: (userId: string, userEmail?: string) => Promise<void>;
  updateIdentityOnLogin: (userId: string, userEmail?: string) => Promise<void>;
  getAttributionReport: (userId: string) => Promise<any>;
}

/**
 * React Hook for UTM tracking and user attribution
 * 利用 PostHog 的自动 UTM 捕获机制
 */
export function useUTMTracking(): UseUTMTrackingReturn {
  const [attribution, setAttribution] = useState<UTMAttribution | null>(null);
  const { user } = useAuthStore();

  // 从 PostHog 获取 UTM 归因数据
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      // PostHog 自动捕获 UTM，我们直接读取
      const currentAttribution = getUTMAttribution();
      setAttribution(currentAttribution);
      
      console.log('UTM attribution from PostHog:', currentAttribution);
    } catch (error) {
      console.error('Failed to get UTM attribution from PostHog:', error);
    }
  }, []);

  // 当用户登录状态改变时，自动更新关联
  useEffect(() => {
    if (user?.id) {
      // PostHog 自动处理身份关联，我们只需要同步到 Supabase
      updateUTMAttributionOnLogin(user.id, user.email);
    }
  }, [user?.id]);

  // 用户注册时关联 UTM（保存到 Supabase）
  const associateWithUser = async (userId: string, userEmail?: string) => {
    try {
      await saveUTMAttributionOnRegistration(userId, userEmail);
      console.log('UTM attribution saved for user:', userId);
    } catch (error) {
      console.error('Failed to save UTM attribution for user:', error);
      throw error;
    }
  };

  // 用户登录时更新身份关联
  const updateIdentityOnLogin = async (userId: string, userEmail?: string) => {
    try {
      await updateUTMAttributionOnLogin(userId, userEmail);
      console.log('UTM attribution updated on login:', userId);
    } catch (error) {
      console.error('Failed to update UTM attribution on login:', error);
      throw error;
    }
  };

  // 获取归因报告
  const getAttributionReport = async (userId: string) => {
    try {
      return await getUserAttributionReport(userId);
    } catch (error) {
      console.error('Failed to get attribution report:', error);
      throw error;
    }
  };

  return {
    attribution,
    associateWithUser,
    updateIdentityOnLogin,
    getAttributionReport
  };
}

/**
 * Hook for getting current user's attribution data
 * 简化版本：主要从 PostHog 获取数据，Supabase 部分待实现
 */
export function useUserAttribution() {
  const [attributionData, setAttributionData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuthStore();

  const fetchAttributionData = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      // 主要从 PostHog 获取实时数据
      const report = await getUserAttributionReport(user.id);
      setAttributionData(report);
    } catch (error) {
      console.error('Failed to fetch attribution data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttributionData();
  }, [user?.id]);

  return {
    attributionData,
    loading,
    refetch: fetchAttributionData
  };
}